import type { ReactNode } from 'react'
import { useCoachingStore } from '../store'
import { OverallComment } from './overall-comment'
import { RankingBanner } from './ranking-banner'
import type { ReviewBodyProps } from './review-tab'
import { DeepDives } from './sections/deep-dives'
import { HldDiagram } from './sections/hld-diagram'
import { PacingSection } from './sections/pacing-section'
import { PostMortemSection } from './sections/post-mortem-section'
import { RequirementsList } from './sections/requirements-list'

/**
 * System design review body (spec §8.5, URL Shortener seed) — the per-type
 * `sd` entry in `review-tab.tsx`'s `BODY_BY_TYPE` map. Owns the shared
 * sections (ranking banner, pacing, post-mortem, overall — same components
 * A4 built for `DefaultReviewBody`, A5 reuses for `coding`) plus the
 * system-design-only sections in spec §8.5's order: functional
 * requirements, non-functional requirements, high-level design, deep
 * dives, pacing, post-mortem, overall.
 */

const FR_ITEMS = [
  'Given a long URL, return a unique short link',
  'Redirect a short link to its original URL',
  'Users can pick a custom alias (optional)',
  'Links expire — default 5 years, user-configurable',
]

const NFR_ITEMS = [
  'High availability — redirects must not go down (99.99%)',
  'Redirect latency p99 < 100 ms',
  'Scale: 1B stored URLs, 100:1 read-to-write ratio',
  'Short links must not be guessable',
]

export function SystemDesignReview(props: ReviewBodyProps): ReactNode {
  const { submission, getSectionRef } = props
  const { commentsFor } = useCoachingStore().review
  const comments = commentsFor(submission.id)

  return (
    <>
      <RankingBanner ranking={submission.ranking} />
      <div className="flex flex-col gap-3.5">
        <RequirementsList
          sectionKey="fr"
          title="Functional requirements"
          targetLabel="Functional"
          prefix="F"
          items={FR_ITEMS}
          seedSuggestion={{
            id: 'fr-rate-limiting',
            text: 'Rate limiting — cap shorten requests per user / IP',
          }}
          comments={comments}
          anchorRef={getSectionRef('fr')}
        />
        <RequirementsList
          sectionKey="nfr"
          title="Non-functional requirements"
          targetLabel="Non-functional"
          prefix="N"
          items={NFR_ITEMS}
          seedSuggestion={{
            id: 'nfr-durability',
            text: 'Durability — a stored link must never be lost (DB row TTL ≠ cache TTL)',
          }}
          comments={comments}
          anchorRef={getSectionRef('nfr')}
        />
        <HldDiagram
          submissionId={submission.id}
          comments={comments}
          anchorRef={getSectionRef('hld')}
        />
        <DeepDives
          submissionId={submission.id}
          comments={comments}
          anchorRef={getSectionRef('deep')}
        />
        <PacingSection
          pacing={submission.pacing}
          comments={comments}
          anchorRef={getSectionRef('pacing')}
        />
        <PostMortemSection
          postMortem={submission.postMortem}
          comments={comments}
          anchorRef={getSectionRef('postmortem')}
        />
        <div ref={getSectionRef('overall')}>
          <OverallComment submissionId={submission.id} />
        </div>
      </div>
    </>
  )
}
